import _ from "lodash";
import React from "react";
import {
  getAvatarData,
  getIsLearningCoursesEnabled,
} from "LearningCourses/modules/Utils";
import {
  getLearningCourseDetailsFromCache,
  getLearningCourseGradingPeriodsFromCache,
} from "LearningCourse/modules/LearningCourseGraphqlHelpers";
import { getOrganizationCurriculumProgramsFromCache } from "modules/CommonGraphqlHelpers";
import {
  BLUEPRINT_TEACHER_COURSE_SYNC_PERMISSION,
  CURRICULUM_PROGRAM_TO_LEARNING_STANDARD_MAP,
} from "./LearningCourseStringConstants";
import { Avatar } from "@toddle-edu/ds-web";
import {
  MANAGE_LOCK_CONFIGURATION_PERMISSION,
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  CAN_EDIT_COURSE_FLOW_PERMISSION,
} from "Constants/permissionConstants";
import ACLStorage from "lib/aclService";
import ACLStore from "lib/aclStore";
import { store } from "store/createStore";
import { getSelectedCurriculumId, getUserType } from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule";
import { isUserTypeStaff } from "Utils";
import { generateRnPContextsByLevel } from "Utils/RnPHelpers";
import {
  getIsCoursePlanRouteEnabled,
  getIsTimetablePlannerEnabled,
} from "Utils/ACLStoreUtils";
import {
  DashedCircleOutlined,
  DashedCircleMinusOutlined,
  SyncCompleteOutlined,
  SyncPendingOutlined,
} from "@toddle-edu/ds-icons";
import { getIsGradingPeriodUnlocked } from "GradingSetup/modules/Utils";

export const UNIT = "unit";
export const UNIT_PLANS = "unitPlans";
export const COURSE_PLAN = "course-plan";
export const FLOW = "flow";
export const CARD = "card";
export const TABLE = "table";
export const TIMELINE = "timeline";
export const SCHEDULE = "schedule";
export const COURSE_FLOW = "course-flow";
export const CARD_VIEW = "cardView";
export const TIMELINE_VIEW = "timelineView";
export const COURSE_OVERVIEW = "courseOverview";

export const LEARNING_COURSE_SYNC_CHANGES = "learning-course-sync-changes";

export const getSelectedLearningCourseId = state => {
  const userInfo = getUserInfo();
  const userType = userInfo.user_type;
  const isUserTeacher = isUserTypeStaff({ userType });

  return isUserTeacher
    ? _.get(state, "learningCourse.selectedLearningCourse.id", undefined)
    : _.get(state, "teacher.selected_class.learningCourse.id", undefined);
};

export const getSelectedLearningCourse = state => {
  return _.get(state, "learningCourse.selectedLearningCourse", undefined);
};

// Resolve the base curriculum (sibling `isPrimary` curriculum program of the
// same type) for a learning course. Prefers the course's own curriculum
// program; falls back to the org's globally-active program so behavior is
// unchanged outside a LearningCourse route.
//
// Reads `curriculumPrograms` from the Apollo cache (`getOrganizationCurriculumProgramsQuery`
// — populated by the LearningCourseWrapper HOC), and falls back to redux state
// for legacy paths where the cache isn't warm. No new fetch is triggered here.
export const getBaseCurriculumForLearningCourse = ({
  state,
  learningCourse,
  organizationId,
}) => {
  const courseProgram = _.get(learningCourse, "curriculumProgram");
  const currentProgram = _.get(state, "platform.currentCurriculumProgram");

  const type = _.get(courseProgram, "type") || _.get(currentProgram, "type");
  const currentId = _.get(courseProgram, "id") || _.get(currentProgram, "id");

  if (!type || !currentId) return null;

  const cacheData = organizationId
    ? getOrganizationCurriculumProgramsFromCache(organizationId)
    : null;
  const programs =
    _.get(cacheData, "curriculumPrograms") ||
    _.get(state, "platform.organizationCurriculumPrograms") ||
    [];

  return (
    _.find(
      programs,
      program =>
        program.id !== currentId && program.type === type && program.isPrimary
    ) || null
  );
};

export const isLearningCourseModuleVisible = ({
  permission,
  learningCourseId = null,
}) => {
  if (!permission) return true;

  const state = store?.getState();

  const curriculumProgramId = getSelectedCurriculumId(state);

  const selectedLearningCourseId =
    learningCourseId || getSelectedLearningCourseId(state);

  const contexts = generateRnPContextsByLevel({
    level: "LEARNING_COURSE",
    curriculumProgramId,
    learningCourseId: selectedLearningCourseId,
  });

  const isVisible = ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)
    ? ACLStorage.getModuleSettingsValueByContext(permission, contexts[0])
    : ACLStorage.getModuleSettingsValue(permission);

  return isVisible;
};

export const isClassModuleVisible = ({
  classId,
  permission,
  learningCourseId,
  addOnContexts,
}) => {
  if (!permission) return true;

  const state = store?.getState();

  const curriculumProgramId = getSelectedCurriculumId(state);

  const selectedLearningCourseId =
    learningCourseId || getSelectedLearningCourseId(state);

  const contexts = generateRnPContextsByLevel({
    level: "COURSE",
    curriculumProgramId,
    learningCourseId: selectedLearningCourseId,
    courseId: classId,
  });

  /**
   * 1. We use 'getModuleSettingsValueByContext' instead of 'getModuleSettingsValue'
   *    because the sidebar for `CLASS_LEVEL` modules is generated outside the context of a specific class.
   *
   * 2. `LEARNING_COURSE - Toddle 2.0` is only enabled when RnP is already
   *    active for the organization. However, as a fallback for the older configuration architecture,
   *    we also check control parameters wrt `LEARNING_COURSE + USER` or `CURRICULUM_PROGRAM + USER` when RnP is disabled.
   *    We cannot check wrt `COURSE + USER` for this because the configuration query only runs once
   *    we're already inside a specific class.
   */

  const isVisible = ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)
    ? ACLStorage.getModuleSettingsValueByContext(permission, contexts[0], {
        addOnContexts,
      })
    : ACLStorage.getModuleSettingsValue(permission);

  return isVisible;
};

/*
  Handles if a user hits unavailable learning course id
  or a learning course of different curriculum program
*/
export const getIsValidLearningCourseId = ({
  curriculumProgramId,
  learningCourseDetails,
}) => {
  const selectedCurriculumProgramId = _.get(
    learningCourseDetails,
    "curriculumProgram.id",
    ""
  );

  return selectedCurriculumProgramId === curriculumProgramId;
};

/**
 * A same copy of below function also exists in `lib/aclService/modules/Utils`
 * This is done to avoid circular dependency issue
 * If we by pass it this time, we still risk encountering many more such dependencies in the future due to the change.
 * Hence, we decided to create a copy of this function.
 * If any changes are made here, please make sure to update the other copy as well
 */

export const getLearningCourseVariables = ({
  learningCourseId = null,
  academicYearId,
  includeLearningCourseGradeFilters = true,
}) => {
  const userId = getUserInfo()?.id;
  const academicYears = academicYearId ? [academicYearId] : [];

  return {
    learningCourseId,
    classFilters: {
      status: "ALL",
      userId,
      academicYears,
    },
    termFilters: {
      types: ["REPORTING", "EXAM"],
    },
    ...(includeLearningCourseGradeFilters && {
      gradeFilters: {
        academicYearIds: [academicYearId],
      },
    }),
  };
};

export const getLearningCoursesListVariables = ({
  curriculumProgramIds,
  organizationId,
  academicYearId,
}) => {
  return {
    organizationId,
    filters: {
      curriculumProgramIds,
      fetchAllCourses: true,
      academicYears: [academicYearId],
      states: ["ACTIVE"], // Default status
    },
  };
};

export const FIELD_OPTIONS = {
  search: {
    key: "search",
    allowedViewTypes: [
      FLOW,
      CARD,
      TABLE,
      TIMELINE,
      COURSE_FLOW,
      CARD_VIEW,
      TIMELINE_VIEW,
      COURSE_OVERVIEW,
    ],
  },
  filters: {
    key: "filters",
    allowedViewTypes: [
      FLOW,
      CARD,
      TABLE,
      TIMELINE,
      COURSE_FLOW,
      CARD_VIEW,
      TIMELINE_VIEW,
      COURSE_OVERVIEW,
    ],
  },
  classes_filter: {
    key: "classes_filter",
    allowedViewTypes: [
      FLOW,
      CARD,
      TABLE,
      TIMELINE,
      COURSE_FLOW,
      CARD_VIEW,
      TIMELINE_VIEW,
      COURSE_OVERVIEW,
    ],
  },
  unit_display_filters: {
    key: "unit_display_filters",
    allowedViewTypes: [
      CARD,
      TABLE,
      TIMELINE,
      CARD_VIEW,
      TIMELINE_VIEW,
      COURSE_OVERVIEW,
    ],
  },
  course_display_filters: {
    key: "course_display_filters",
    allowedViewTypes: [COURSE_FLOW, FLOW],
  },
  table_configure_settings: {
    key: "table_configure_settings",
    allowedViewTypes: [TABLE, COURSE_OVERVIEW],
  },
  download: {
    key: "download",
    allowedViewTypes: [TABLE, COURSE_OVERVIEW],
  },
  create: {
    key: "create",
    allowedViewTypes: [
      FLOW,
      CARD,
      TABLE,
      TIMELINE,
      COURSE_FLOW,
      CARD_VIEW,
      TIMELINE_VIEW,
      COURSE_OVERVIEW,
    ],
  },
};

export const getViewFilterOptions = ({
  isUserTeacher,
  isBlueprintCourse,
  enabledViews,
  isCASandEEBasedDPCurriculum = false,
}) => {
  const isCoursePlanRouteEnabled = getIsCoursePlanRouteEnabled();

  const flow = isCoursePlanRouteEnabled ? FLOW : COURSE_FLOW;
  const card = isCoursePlanRouteEnabled ? CARD : CARD_VIEW;
  const timeline = isCoursePlanRouteEnabled ? TIMELINE : TIMELINE_VIEW;
  const table = isCoursePlanRouteEnabled ? TABLE : COURSE_OVERVIEW;

  const isTimetablePlannerEnabled = getIsTimetablePlannerEnabled();

  const options = _.compact([
    {
      key: flow,
      label: "course:flow",
      value: flow,
    },
    !isCASandEEBasedDPCurriculum && {
      value: card,
      label: "unitPlan:unit_card_view",
      key: card,
    },
    !isCASandEEBasedDPCurriculum &&
      !isBlueprintCourse && {
        value: timeline,
        label: "unitPlan:unit_timeline_view",
        key: timeline,
      },
    !isCASandEEBasedDPCurriculum &&
      isUserTeacher && {
        value: table,
        label: "unitPlan:unit_table_view",
        key: table,
      },
    !isBlueprintCourse &&
      isTimetablePlannerEnabled && {
        value: SCHEDULE,
        label: "common:timetable",
        key: SCHEDULE,
      },
  ]);

  if (isCoursePlanRouteEnabled) {
    const filteredOptions = _.filter(
      options,
      option => enabledViews[option.key]
    );

    // Sort by the order in enabledViews object
    const viewKeys = _.keys(enabledViews);
    return _.sortBy(filteredOptions, ({ key }) => _.indexOf(viewKeys, key));
  } else return options;
};

// generate FilterOptions
export const generateFilterOptions = ({
  t,
  filters,
  studentList,
  contentTypeOptions,
  studentAssignmentStatusOptions,
  unitTypeOptions,
  subjectOptions,
  isBlueprintCourse = false,
  isSubjectBasedGrading = false,
  currentCourseViewType = [],
}) => {
  const options = {};
  const defaultSelectedOptions = {};

  const userInfo = getUserInfo();
  const userType = userInfo.user_type;
  const isUserTeacher = isUserTypeStaff({ userType });

  const isCourseFlowRoute = _.includes(
    [COURSE_FLOW, FLOW],
    currentCourseViewType
  );

  const populateOptions = ({
    filterName,
    filterOptions,
    title,
    shouldPopulate = true,
    isSingleSelect = false,
  }) => {
    if (!shouldPopulate) return;

    options[filterName] = {
      options: filterOptions,
      isSingleSelect,
      title: title,
    };

    const filtersValue = _.get(filters, filterName, []);

    if (filtersValue === "ALL") {
      defaultSelectedOptions[filterName] = _.flatMap(filterOptions, option => {
        const nestedOptions = option.options;
        if (nestedOptions) return _.map(nestedOptions, "value");
        return option.value;
      });
    } else {
      defaultSelectedOptions[filterName] = filtersValue;
    }
  };

  // Unit type
  populateOptions({
    filterName: "unitType",
    filterOptions: unitTypeOptions,
    title: t("common:unit_type_label"),
    shouldPopulate: _.size(unitTypeOptions) > 1 && isSubjectBasedGrading,
  });

  // Content type
  populateOptions({
    filterName: "contentTypes",
    filterOptions: contentTypeOptions,
    title: t("classMaterials:item_types_label"),
    shouldPopulate: isCourseFlowRoute,
  });

  // Status
  populateOptions({
    filterName: "studentAssignmentStatus",
    filterOptions: studentAssignmentStatusOptions,
    title: t("common:status"),
    shouldPopulate: !isUserTeacher && !isBlueprintCourse && isCourseFlowRoute,
  });

  // Students
  populateOptions({
    filterName: "students",
    filterOptions: studentList,
    title: t("common:shared_with"),
    shouldPopulate:
      !_.isEmpty(studentList) &&
      isUserTeacher &&
      !isBlueprintCourse &&
      isCourseFlowRoute,
  });

  // subjects
  populateOptions({
    filterName: "subjects",
    filterOptions: subjectOptions,
    title: t("common:subject_plural"),
    shouldPopulate: _.size(subjectOptions) > 1 && isSubjectBasedGrading,
  });

  return { options, defaultSelectedOptions };
};

// course viewer/creator permissions
const canEditItems = () => {
  return ACLStorage.getPermissionValue(CAN_EDIT_COURSE_FLOW_PERMISSION);
};
export const getManageMode = () => (canEditItems() ? "EDIT" : "VIEW");
export const getAccessMode = ({
  // isUserStudentOrParent,
  mode,
  // isCurrentAcademicYear,
  isViewOnlyMode,
}) => {
  let modeAsPerUser = mode;
  if (isViewOnlyMode) {
    modeAsPerUser = "VIEW";
  }

  // This will be used for student and parent end

  // if (isUserStudentOrParent) {
  //   if (isCurrentAcademicYear) {
  //     modeAsPerUser = "EDIT";
  //   }
  //   modeAsPerUser = "STUDENT_" + modeAsPerUser;
  // }
  return modeAsPerUser;
};

const getClassOptionsForDropdown = ({ classesList = [] }) => {
  // Generate class options for dropdown
  const classOptions = _.map(classesList, classItem => {
    const { id, title, profileImageData } = classItem;
    const { color, name, AvatarIcon, iconType } = getAvatarData({
      id,
      title,
      profileImageData,
    });

    return {
      key: id,
      label: title,
      icon: (
        <Avatar
          shape="square"
          icon={<AvatarIcon size="small" />}
          color={color}
          name={name}
          iconType={iconType}
          dsVersion={"2.0"}
        />
      ),
      iconSize: "small",
    };
  });

  return classOptions;
};

export const getDropdownClassesList = ({
  learningCourseId,
  academicYearId,
  permission,
  addOnContexts,
}) => {
  let classesList = [];

  if (!getIsStaffUserWithLearningCourseEnabled()) {
    return null;
  }

  if (learningCourseId) {
    const learningCourseVariables = getLearningCourseVariables({
      learningCourseId,
      academicYearId,
    });
    const learningCourseDetails = getLearningCourseDetailsFromCache(
      learningCourseVariables
    );

    classesList = _.get(learningCourseDetails, "classes", []);
  }

  classesList = _.filter(classesList, classItem => {
    const { id } = classItem;
    const isVisible = isClassModuleVisible({
      classId: id,
      permission,
      learningCourseId,
      addOnContexts,
    });

    return isVisible;
  });

  return classesList;
};

export const generateClassDropdownMenuProps = ({
  classesList = [],
  selectedClassId = "",
  onClassChange = () => {},
}) => {
  if (_.size(classesList) < 2) {
    return null;
  }

  return {
    value: selectedClassId,
    showSelection: true,
    options: getClassOptionsForDropdown({
      classesList,
    }),
    onClick: onClassChange,
  };
};

export const checkIfTermExistsForCurrentLearningCourse = ({
  learningCourseId,
  academicYearId,
  learningCourseDetails: _learningCourseDetails,
}) => {
  const state = store?.getState();

  const isToddleLibrary = _.get(state, "login.activeTab") == "TODDLE_LIBRARY";
  if (isToddleLibrary) {
    return true;
  }

  let learningCourseDetails = _learningCourseDetails;

  if (_.isEmpty(learningCourseDetails)) {
    const learningCourseVariables = getLearningCourseVariables({
      learningCourseId,
      academicYearId,
    });

    learningCourseDetails = getLearningCourseDetailsFromCache(
      learningCourseVariables
    );
  }

  // When unit plan is opened outside course flow context (eg. from school library)...
  // we get data from getLearningCourseGradingPeriodsQuery hooked in UnitPlanDetails component
  if (_.isEmpty(learningCourseDetails)) {
    learningCourseDetails = getLearningCourseGradingPeriodsFromCache({
      learningCourseId,
      filters: {
        types: ["REPORTING", "EXAM"],
      },
    });
  }

  const gradingPeriods = _.get(learningCourseDetails, "gradingPeriods", {});
  const totalGradingPeriods = _.get(gradingPeriods, "totalCount", 0);

  return totalGradingPeriods > 0;
};

export const checkIfUnlockedTermExistsForLearningCourse = ({
  learningCourseId,
  academicYearId,
  learningCourseDetails: _learningCourseDetails,
}) => {
  const state = store?.getState();

  const isToddleLibrary = _.get(state, "login.activeTab") == "TODDLE_LIBRARY";
  if (isToddleLibrary) {
    return true;
  }

  let learningCourseDetails = _learningCourseDetails;

  if (_.isEmpty(learningCourseDetails)) {
    const learningCourseVariables = getLearningCourseVariables({
      learningCourseId,
      academicYearId,
    });

    learningCourseDetails = getLearningCourseDetailsFromCache(
      learningCourseVariables
    );
  }

  // When unit plan is opened outside course flow context (eg. from school library)...
  // we get data from getLearningCourseGradingPeriodsQuery hooked in UnitPlanDetails component
  if (_.isEmpty(learningCourseDetails)) {
    learningCourseDetails = getLearningCourseGradingPeriodsFromCache({
      learningCourseId,
      filters: {
        types: ["REPORTING", "EXAM"],
      },
    });
  }

  const gradingPeriods = _.get(
    learningCourseDetails,
    "gradingPeriods.edges",
    []
  );

  if (_.isEmpty(gradingPeriods)) {
    return true;
  }

  return _.some(gradingPeriods, gradingPeriod =>
    getIsGradingPeriodUnlocked(_.get(gradingPeriod, "node"))
  );
};

export const getIsStaffUserWithLearningCourseEnabled = () => {
  const userType = getUserType();
  const isStaffUser = isUserTypeStaff({ userType });
  return isStaffUser && getIsLearningCoursesEnabled();
};

export const isBlueprintTeacherCourseAssociationEnabled = () => {
  return ACLStore.can(BLUEPRINT_TEACHER_COURSE_SYNC_PERMISSION);
};

export const isBluePrintTeacherCourseSyncEnabled = () => {
  return (
    getIsSyncAndLockAllowed() && isBlueprintTeacherCourseAssociationEnabled()
  );
};

export const getIsUnitFlowSharingEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableSharingInUnitFlow");
};

export const getisCourseAssociatedWithBlueprint = learningCourse => {
  return Boolean(_.get(learningCourse, "bluePrintCourse.id", null));
};

export const generateClassContexts = ({
  classes,
  learningCourseId,
  curriculumProgramId,
  primaryGradeId,
}) => {
  const contexts = _.map(classes, ({ id }) => {
    const classContexts = generateRnPContextsByLevel({
      level: "COURSE",
      courseId: id,
      learningCourseId,
      curriculumProgramId,
      gradeId: primaryGradeId,
    });
    return _.head(classContexts);
  });

  return contexts;
};

export const getLearningCoursePermissionsQueryVariables = () => {
  return {
    filters: {
      module: "LEARNING_COURSE",
      appType: ["TEACHER_WEB"],
    },
  };
};

export const getClassesWithLearningCoursesPermissionsQueryVariables = ({
  contexts,
}) => {
  return {
    filters: {
      module: "CLASSES_WITH_LEARNING_COURSES",
      appType: ["TEACHER_WEB"],
      contexts,
    },
  };
};

export const getIAEAPermissionsQueryVariables = ({ contexts }) => {
  return {
    filters: {
      module: "IAEA",
      appType: ["TEACHER_WEB"],
      contexts,
    },
  };
};

export const getLearningCoursePermissionsQuerySkipCondition = ({
  primaryGradeId,
}) => !primaryGradeId;

export const getClassesWithLearningCoursesPermissionsQuerySkipCondition = ({
  learningCourseDetails,
  primaryGradeId,
}) => {
  return (
    _.isEmpty(_.get(learningCourseDetails, "classes", [])) || !primaryGradeId
  );
};

export const getIAEAPermissionsQuerySkipCondition = ({
  learningCourseDetails,
  primaryGradeId,
}) => {
  const classes = _.get(learningCourseDetails, "classes", []);

  if (_.isEmpty(classes) || !primaryGradeId) {
    return true;
  }

  const hasProjectGroups = _.some(
    classes,
    classItem => !_.isEmpty(_.get(classItem, "projectGroups", []))
  );

  return !hasProjectGroups;
};

export const SYNC_STATUS = {
  MIXED: "MIXED",
  IN_SYNC: "IN_SYNC",
  CANNOT_SYNC: "CANNOT_SYNC",
  PENDING_SYNC: "PENDING_SYNC",
  NOTHING_TO_SYNC: "NOTHING_TO_SYNC",
};

export const SYNC_STATUS_CONFIG = {
  [SYNC_STATUS.MIXED]: {
    id: SYNC_STATUS.MIXED,
    locales: "-",
    iconV2: <DashedCircleMinusOutlined variant="warning" />,
    backgroundColor: "var(--surface-secondary-enabled)",
    localesV2: "course:partially_synced",
    tooltips: ["course:partially_synced_filter_tooltip"],
  },
  [SYNC_STATUS.NOTHING_TO_SYNC]: {
    id: SYNC_STATUS.NOTHING_TO_SYNC,
    locales: "-",
    backgroundColor: "var(--surface-secondary-enabled)",
  },
  [SYNC_STATUS.CANNOT_SYNC]: {
    id: SYNC_STATUS.CANNOT_SYNC,
    locales: "course:cannot_sync",
    icon: <SyncPendingOutlined variant="critical" />,
    backgroundColor: "var(--decorative-background-subtle-pink)",
    tooltips: [
      "course:cannot_sync_modified_filter_tooltip",
      "course:cannot_sync_reset_filter_tooltip",
    ],
  },
  [SYNC_STATUS.IN_SYNC]: {
    id: SYNC_STATUS.IN_SYNC,
    locales: "course:synced",
    icon: <SyncCompleteOutlined variant="success" />,
    backgroundColor: "var(--decorative-background-subtle-teal)",
    tooltips: ["course:in_sync_filter_tooltip"],
  },

  [SYNC_STATUS.PENDING_SYNC]: {
    id: SYNC_STATUS.PENDING_SYNC,
    locales: "course:pending_sync",
    icon: <DashedCircleOutlined variant="subtle" />,
    backgroundColor: "var(--decorative-background-subtle-yellow)",
    tooltips: ["course:pending_sync_filter_tooltip"],
  },
};

export const PAGE_LEVEL_SYNC_STATUS_TOOLTIP_MAP = {
  [SYNC_STATUS.MIXED]: "course:partial_synced_tooltip",
  [SYNC_STATUS.PENDING_SYNC]: "course:pending_synced_tooltip",
  [SYNC_STATUS.IN_SYNC]: "course:in_sync_tooltip",
  [SYNC_STATUS.CANNOT_SYNC]: "course:cannot_sync_tooltip",
};

export const getIsBlueprintSyncRevampEnabled = () => {
  return ACLStore.can("FeatureFlag:BlueprintSyncRevamp");
};

export const getIsBlueprintSyncHistoryV2Enabled = () => {
  return ACLStore.can("FeatureFlag:BPSyncHistoryV2");
};

export const getIsSyncAndLockAllowed = () => {
  return ACLStorage.getPermissionValue(MANAGE_LOCK_CONFIGURATION_PERMISSION);
};

export const getCanChangeOwner = () => {
  return ACLStore.can("FeatureFlag:ChangeUnitOwner");
};

export const getCurrentPlannerElementSetForStandardsFromState = state => {
  const { nodeInterfaceType: curriculumInterfaceType } = _.get(
    state,
    "platform.currentCurriculumProgram",
    {}
  );

  const elementType =
    CURRICULUM_PROGRAM_TO_LEARNING_STANDARD_MAP[curriculumInterfaceType];

  const currentPlannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    {}
  );

  const plannerElementSet = currentPlannerElementSets[elementType] || {};

  return plannerElementSet;
};
